import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingContact from '@/components/FloatingContact';
import SEOHead, { generateLocalBusinessSchema } from '@/components/SEOHead';
import { Phone, MapPin, Clock, MessageCircle, Mail, ArrowRight } from 'lucide-react';

const LOCATIONS = {
  houston: {
    en: {
      city: 'Houston',
      fullTitle: 'Windows & Doors in Houston, TX',
      metaDesc: 'Premium window and door systems for modern homes and commercial projects in Houston, Texas. Sliding doors, folding doors, fixed windows, and custom systems.',
      heroTitle: 'Windows & Doors in Houston, Texas',
      heroSub: 'Premium systems for contemporary architecture in the Greater Houston area.',
      content: 'Blackline Premium Solutions serves Houston and surrounding areas with premium window and door systems. From the Heights to Memorial, Tanglewood to River Oaks, our modern fenestration systems are transforming Houston\'s architectural landscape.',
      areas: 'Greater Houston, The Heights, Memorial, River Oaks, Tanglewood, West University, Bellaire, Sugar Land, The Woodlands, Katy',
      whyTitle: 'Why Houston Homeowners Choose Blackline',
      whyPoints: [
        'Systems engineered for Houston\'s hot, humid climate with superior sealing and thermal performance.',
        'Hurricane-rated glass options available for coastal proximity and storm protection.',
        'Energy-efficient designs that help reduce cooling costs in Texas summers.',
        'Local presence with dedicated Houston team for fast response and installation support.'
      ]
    },
    es: {
      city: 'Houston',
      fullTitle: 'Ventanas y Puertas en Houston, TX',
      metaDesc: 'Sistemas premium de ventanas y puertas para hogares modernos y proyectos comerciales en Houston, Texas.',
      heroTitle: 'Ventanas y Puertas en Houston, Texas',
      heroSub: 'Sistemas premium para arquitectura contempor\u00e1nea en el \u00e1rea de Houston.',
      content: 'Blackline Premium Solutions sirve a Houston y \u00e1reas circundantes con sistemas premium de ventanas y puertas. Desde The Heights hasta Memorial, nuestros sistemas de fenestraci\u00f3n moderna est\u00e1n transformando el paisaje arquitect\u00f3nico de Houston.',
      areas: 'Gran Houston, The Heights, Memorial, River Oaks, Tanglewood, West University, Bellaire, Sugar Land, The Woodlands, Katy',
      whyTitle: 'Por Qu\u00e9 los Propietarios de Houston Eligen Blackline',
      whyPoints: [
        'Sistemas dise\u00f1ados para el clima c\u00e1lido y h\u00famedo de Houston con sellado superior.',
        'Opciones de vidrio resistente a huracanes disponibles.',
        'Dise\u00f1os eficientes energ\u00e9ticamente que reducen costos de enfriamiento.',
        'Presencia local con equipo dedicado en Houston para respuesta r\u00e1pida.'
      ]
    },
    phone: '(346) 615-6860',
    mapUrl: 'https://maps.google.com/?q=Blackline+Premium+Solutions+Houston+TX'
  },
  dallas: {
    en: {
      city: 'Dallas / Fort Worth',
      fullTitle: 'Windows & Doors in Dallas, TX',
      metaDesc: 'Premium window and door systems for modern homes and commercial projects in Dallas-Fort Worth, Texas. Custom configurations and expert installation.',
      heroTitle: 'Windows & Doors in Dallas, Texas',
      heroSub: 'Premium systems for contemporary architecture in the Dallas-Fort Worth Metroplex.',
      content: 'Blackline Premium Solutions brings premium fenestration to the Dallas-Fort Worth Metroplex. From Highland Park to Preston Hollow, Southlake to Frisco, our systems deliver the modern aesthetic and performance that North Texas architects and builders demand.',
      areas: 'Dallas, Fort Worth, Highland Park, Preston Hollow, Southlake, Frisco, Plano, Allen, McKinney, Prosper, Westlake',
      whyTitle: 'Why Dallas Homeowners Choose Blackline',
      whyPoints: [
        'Modern profiles that complement the contemporary architecture thriving across DFW.',
        'Thermal break technology for energy efficiency in North Texas\u2019 hot summers and cold fronts.',
        'Wide product range from slim-profile sliders to dramatic folding door systems.',
        'Dedicated DFW team ensuring consistent supply and local project support.'
      ]
    },
    es: {
      city: 'Dallas / Fort Worth',
      fullTitle: 'Ventanas y Puertas en Dallas, TX',
      metaDesc: 'Sistemas premium de ventanas y puertas para hogares modernos en Dallas-Fort Worth, Texas.',
      heroTitle: 'Ventanas y Puertas en Dallas, Texas',
      heroSub: 'Sistemas premium para arquitectura contempor\u00e1nea en el Metroplex de Dallas-Fort Worth.',
      content: 'Blackline Premium Solutions trae fenestraci00f3n premium al Metroplex de Dallas-Fort Worth. Desde Highland Park hasta Southlake, nuestros sistemas entregan la est\u00e9tica moderna y el rendimiento que arquitectos y constructores del Norte de Texas exigen.',
      areas: 'Dallas, Fort Worth, Highland Park, Preston Hollow, Southlake, Frisco, Plano, Allen, McKinney, Prosper, Westlake',
      whyTitle: 'Por Qu\u00e9 los Propietarios de Dallas Eligen Blackline',
      whyPoints: [
        'Perfiles modernos que complementan la arquitectura contempor\u00e1nea de DFW.',
        'Tecnolog\u00eda de ruptura t\u00e9rmica para eficiencia energ\u00e9tica.',
        'Amplia gama de productos desde corredizas hasta puertas plegables dram\u00e1ticas.',
        'Equipo dedicado en DFW asegurando suministro consistente.'
      ]
    },
    phone: '(817) 886-0243',
    mapUrl: 'https://maps.google.com/?q=Blackline+Premium+Solutions+Dallas+TX'
  }
};

const LocationPage = () => {
  const { city } = useParams();
  const { lang, getLocalizedPath } = useLanguage();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);

  const locationData = LOCATIONS[city] || LOCATIONS.houston;
  const loc = locationData[lang] || locationData.en;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, settingsRes] = await Promise.all([api.get('/products'), api.get('/site-settings')]);
        setProducts(prodRes.data);
        setSettings(settingsRes.data);
      } catch (err) { console.error(err); }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [city]);

  const contact = settings?.contact || {};

  return (
    <div data-testid="location-page">
      <SEOHead
        title={loc.fullTitle}
        description={loc.metaDesc}
        path={`/locations/${city}`}
        schema={generateLocalBusinessSchema(loc.city, locationData.phone)}
      />
      <Navbar />

      <section className="bl-subpage-hero">
        <div className="bl-subpage-hero-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80)' }} />
        <div className="bl-subpage-hero-content">
          <div className="eyebrow">{loc.city}</div>
          <h1>{loc.heroTitle}</h1>
          <p>{loc.heroSub}</p>
        </div>
      </section>

      <div className="bl-info-content">
        <p style={{ fontSize: '1.1rem', lineHeight: '1.9' }}>{loc.content}</p>
        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--cream)', borderLeft: '3px solid var(--gold)' }}>
          <strong style={{ fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            {lang === 'es' ? '\u00c1reas de Servicio' : 'Service Areas'}
          </strong>
          <p style={{ marginTop: '0.5rem', color: 'var(--gray-600)' }}>{loc.areas}</p>
        </div>
      </div>

      {/* Why Section */}
      <div className="bl-advantages-grid">
        <div style={{ gridColumn: '1 / -1', marginBottom: '1rem' }}>
          <div className="bl-section-eyebrow">{loc.city}</div>
          <div className="bl-section-title">{loc.whyTitle}</div>
        </div>
        {loc.whyPoints.map((point, i) => (
          <div className="bl-advantage" key={i}>
            <h3>{String(i + 1).padStart(2, '0')}</h3>
            <p>{point}</p>
          </div>
        ))}
      </div>

      {/* Contact CTA */}
      <div className="bl-product-cta">
        <h2>{lang === 'es' ? `Cont\u00e1ctanos en ${loc.city}` : `Contact Us in ${loc.city}`}</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>{locationData.phone}</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={`tel:+1${locationData.phone.replace(/\D/g, '')}`} className="btn-gold">
            <Phone size={16} /> {lang === 'es' ? 'Llamar' : 'Call Now'}
          </a>
          <a href={`https://wa.me/${contact.whatsapp || '13466156860'}`} className="btn-ghost" target="_blank" rel="noopener noreferrer">
            <MessageCircle size={16} /> WhatsApp
          </a>
          <a href={locationData.mapUrl} className="btn-ghost" target="_blank" rel="noopener noreferrer">
            <MapPin size={16} /> {lang === 'es' ? 'Direcciones' : 'Get Directions'}
          </a>
        </div>
      </div>

      <Footer products={products} settings={settings} />
      <FloatingContact settings={settings} />
    </div>
  );
};

export default LocationPage;
